// Blocking modal for pending or outdated account legal policy acceptance.

import { useMemo, useState } from 'react';
import { RevoltModal } from './RevoltModal';
import { RevoltButton } from './RevoltButton';
import { RevoltCheckbox } from './RevoltCheckbox';
import {
  legalDocuments,
  formatLegalPolicyVersion,
} from '../sections/account/legalDocuments';
import { LegalDocumentModal } from '../sections/account/components/LegalDocumentModal';
import './LegalAcceptanceRequiredModal.css';

function getDocumentsNeedingAcceptance(legalAcceptance) {
  if (!legalAcceptance) {
    return legalDocuments.map((doc) => ({ ...doc, status: 'pending' }));
  }
  const pending = new Set(legalAcceptance.pending || []);
  const outdated = new Set(legalAcceptance.outdated || []);
  return legalDocuments
    .filter((doc) => pending.has(doc.slug) || outdated.has(doc.slug))
    .map((doc) => ({
      ...doc,
      status: outdated.has(doc.slug) ? 'outdated' : 'pending',
    }));
}

export function LegalAcceptanceRequiredModal({
  isOpen,
  legalAcceptance,
  onAccept,
  onLogout,
  submitting = false,
  error = '',
}) {
  const needed = useMemo(
    () => getDocumentsNeedingAcceptance(legalAcceptance),
    [legalAcceptance]
  );
  const [checked, setChecked] = useState({});
  const [viewDocument, setViewDocument] = useState(null);
  const [localError, setLocalError] = useState('');

  const allChecked =
    needed.length > 0 &&
    needed.every((doc) => checked[doc.slug] === true);

  const hasOutdated = needed.some((doc) => doc.status === 'outdated');

  const toggleChecked = (slug) => {
    setChecked((prev) => ({ ...prev, [slug]: !prev[slug] }));
    setLocalError('');
  };

  const handleSubmit = async () => {
    if (!allChecked) {
      setLocalError('Please confirm each policy before continuing.');
      return;
    }
    setLocalError('');
    await onAccept({ slugs: needed.map((d) => d.slug) });
  };

  const displayError = error || localError;

  return (
    <>
      <RevoltModal
        isOpen={isOpen}
        onClose={() => {}}
        title={hasOutdated ? 'Updated policies require your acceptance' : 'Accept policies to continue'}
        showCloseButton={false}
        closeOnBackdrop={false}
        containerClassName="legal-acceptance-modal"
      >
        <p className="legal-acceptance-modal__intro">
          {hasOutdated
            ? 'One or more policies have been updated. Review and accept the current versions to keep using Revolt.'
            : 'Before you continue, review and accept the updated policies that apply to your account.'}
        </p>

        <ul className="legal-acceptance-modal__list">
          {needed.map((doc) => (
            <li key={doc.slug} className="legal-acceptance-modal__row">
              <button
                type="button"
                className="legal-acceptance-modal__card"
                onClick={() => setViewDocument(doc)}
                disabled={submitting}
              >
                <span className="legal-acceptance-modal__card-body">
                  <span className="legal-acceptance-modal__doc-name">
                    {doc.displayName}
                  </span>
                  {doc.status === 'outdated' && (
                    <span className="legal-acceptance-modal__badge">Updated</span>
                  )}
                  <span className="legal-acceptance-modal__version">
                    Effective {formatLegalPolicyVersion(doc.requiredVersion)}
                  </span>
                </span>
              </button>
              <RevoltCheckbox
                className="legal-acceptance-modal__check"
                id={`legal-accept-${doc.slug}`}
                ariaLabel={`I accept the ${doc.displayName}`}
                checked={checked[doc.slug] === true}
                onChange={() => toggleChecked(doc.slug)}
                disabled={submitting}
              />
            </li>
          ))}
        </ul>

        {displayError && (
          <p className="legal-acceptance-modal__error" role="alert">
            {displayError}
          </p>
        )}

        <div className="legal-acceptance-modal__actions">
          <RevoltButton
            variant="primary"
            onClick={handleSubmit}
            disabled={!allChecked || submitting}
            style={{ minWidth: '160px' }}
          >
            {submitting ? 'Saving…' : 'Accept and continue'}
          </RevoltButton>
          <RevoltButton
            variant="secondary"
            onClick={onLogout}
            disabled={submitting}
          >
            Sign out
          </RevoltButton>
        </div>
      </RevoltModal>

      <LegalDocumentModal
        document={viewDocument}
        onClose={() => setViewDocument(null)}
      />
    </>
  );
}
